import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_KEY = 'ic_dashboard_theme';
const VISITOR_KEY = 'ic_visitor_theme';
const VISITOR_CHOICE_KEY = 'ic_visitor_seasonal_choice';
export const THEMES = ['original', 'classic', 'marble', 'halloween'];
const THEME_CLASSES = { original: 'theme-original', marble: 'theme-marble', halloween: 'theme-halloween' };

const ThemeCtx = createContext(null);

const readJson = (k) => { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch { return null; } };
const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Mirrors backend appearance_module.resolve_theme for browser-only visitors.
const resolveVisitor = (season) => {
  const base = THEMES.includes(localStorage.getItem(VISITOR_KEY)) ? localStorage.getItem(VISITOR_KEY) : 'classic';
  const st = season?.theme;
  if (!st) return base;
  const choice = readJson(VISITOR_CHOICE_KEY);
  if (choice?.key === season.key && THEMES.includes(choice.theme)) return choice.theme;
  return st;
};

export const AppThemeProvider = ({ children }) => {
  const { user } = useAuth();
  const [season, setSeason] = useState(null);
  const [seasonLoaded, setSeasonLoaded] = useState(false);
  const [theme, setThemeState] = useState(() => localStorage.getItem(CACHE_KEY) || (localStorage.getItem('token') ? 'marble' : 'classic'));
  const loggedIn = Boolean(user && localStorage.getItem('token'));

  useEffect(() => {
    const load = () => axios.get(`${BASE}/public/seasonal`)
      .then((r) => setSeason((prev) => { const next = r.data?.active || null; return prev?.key === next?.key ? prev : next; }))
      .catch(() => {}).finally(() => setSeasonLoaded(true));
    load();
    const id = setInterval(load, 15 * 60 * 1000); // picks up start/end while the app stays open
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!season?.ends_at) return undefined;
    const ms = new Date(season.ends_at).getTime() - Date.now() + 1000;
    if (ms <= 0 || ms > 2 ** 31 - 1) return undefined;
    const id = setTimeout(() => setSeason(null), ms);
    return () => clearTimeout(id);
  }, [season]);

  useEffect(() => {
    if (!seasonLoaded) return;
    if (!loggedIn) { setThemeState(resolveVisitor(season)); return; }
    axios.get(`${BASE}/me/appearance`, { headers: authHeader() })
      .then((r) => { const t = r.data?.dashboard_theme || 'marble'; localStorage.setItem(CACHE_KEY, t); setThemeState(t); })
      .catch(() => {});
  }, [loggedIn, user?.id, season, seasonLoaded]);

  useEffect(() => {
    Object.entries(THEME_CLASSES).forEach(([id, cls]) => document.body.classList.toggle(cls, theme === id));
    document.body.dataset.appTheme = theme;
  }, [theme]);

  const setTheme = useCallback(async (t) => {
    const prev = theme;
    setThemeState(t);
    if (!loggedIn) {
      localStorage.setItem(VISITOR_KEY, t);
      if (season?.theme) localStorage.setItem(VISITOR_CHOICE_KEY, JSON.stringify({ key: season.key, theme: t }));
      return true;
    }
    localStorage.setItem(CACHE_KEY, t);
    try { await axios.put(`${BASE}/me/appearance`, { dashboard_theme: t }, { headers: authHeader() }); return true; }
    catch { setThemeState(prev); localStorage.setItem(CACHE_KEY, prev); return false; }
  }, [theme, loggedIn, season]);

  const value = useMemo(() => ({ theme, setTheme, season, themeClass: THEME_CLASSES[theme] || '' }), [theme, setTheme, season]);
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
};

export const useAppTheme = () => useContext(ThemeCtx);
